import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { licenseService } from '../services/licenseService';
import './LicenseForm.css';

function LicenseForm() {
  const { id } = useParams();
  const navigate = useNavigate();
  const isEdit = !!id;

  const [formData, setFormData] = useState({
    licenseKey: '',
    product: 'adhoc-woocommerce',
    status: 'active',
    expiresAt: '',
    customerName: '',
    customerEmail: '',
    notes: ''
  });

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const loadLicense = async () => {
    try {
      setLoading(true);
      const response = await licenseService.getById(id);
      const license = response.data.license;
      
      setFormData({
        licenseKey: license.licenseKey || '',
        product: license.product || 'adhoc-woocommerce',
        status: license.status || 'active',
        expiresAt: license.expiresAt ? license.expiresAt.split('T')[0] : '',
        customerName: license.customerName || '',
        customerEmail: license.customerEmail || '',
        notes: license.notes || ''
      });
      
      setError('');
    } catch (err) {
      setError(err.response?.data?.message || 'Error al cargar licencia');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (isEdit) {
      loadLicense();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, isEdit]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      const dataToSend = {
        product: formData.product || 'adhoc-woocommerce',
        status: formData.status || 'active',
        expiresAt: formData.expiresAt && formData.expiresAt.trim() !== '' ? formData.expiresAt : null,
        customerName: formData.customerName && formData.customerName.trim() !== '' ? formData.customerName : null,
        customerEmail: formData.customerEmail && formData.customerEmail.trim() !== '' ? formData.customerEmail : null,
        notes: formData.notes && formData.notes.trim() !== '' ? formData.notes : null
      };

      // Solo incluir licenseKey si tiene valor (para crear o editar)
      if (formData.licenseKey && formData.licenseKey.trim() !== '') {
        dataToSend.licenseKey = formData.licenseKey.trim();
      }

      if (isEdit) {
        await licenseService.update(id, dataToSend);
      } else {
        await licenseService.create(dataToSend);
      }

      navigate('/licenses');
    } catch (err) {
      const errorMsg = err.response?.data?.message || 
                       err.response?.data?.error || 
                       err.message || 
                       'Error al guardar licencia';
      setError(errorMsg);
      console.error('Error al guardar:', err.response?.data || err);
    } finally {
      setLoading(false);
    }
  };

  if (loading && isEdit) {
    return <div className="loading-container">Cargando...</div>;
  }

  return (
    <div className="license-form-container">
      <div className="form-header">
        <h1>{isEdit ? 'Editar Licencia' : 'Nueva Licencia'}</h1>
        <button onClick={() => navigate('/licenses')} className="btn btn-secondary">
          Volver
        </button>
      </div>

      {error && <div className="alert alert-error">{error}</div>}

      <form onSubmit={handleSubmit} className="license-form">
        <div className="form-row">
          <div className="form-group">
            <label htmlFor="licenseKey">
              Clave de Licencia
              {!isEdit && <span className="help-text">(dejar vacío para generar automáticamente)</span>}
            </label>
            <input
              type="text"
              id="licenseKey"
              name="licenseKey"
              value={formData.licenseKey}
              onChange={handleChange}
              placeholder="ADHOC-XXXXXXXX-XXXXXXXX-XXXXXXXX"
              disabled={loading}
            />
          </div>

          <div className="form-group">
            <label htmlFor="product">Producto</label>
            <input
              type="text"
              id="product"
              name="product"
              value={formData.product}
              onChange={handleChange}
              required
              disabled={loading}
            />
          </div>
        </div>

        <div className="form-row">
          <div className="form-group">
            <label htmlFor="status">Estado</label>
            <select
              id="status"
              name="status"
              value={formData.status}
              onChange={handleChange}
              required
              disabled={loading}
            >
              <option value="active">Activa</option>
              <option value="inactive">Inactiva</option>
            </select>
          </div>

          <div className="form-group">
            <label htmlFor="expiresAt">
              Fecha de Expiración
              <span className="help-text">(opcional)</span>
            </label>
            <input
              type="date"
              id="expiresAt"
              name="expiresAt"
              value={formData.expiresAt}
              onChange={handleChange}
              disabled={loading}
            />
          </div>
        </div>

        <div className="form-row">
          <div className="form-group">
            <label htmlFor="customerName">Nombre del Cliente</label>
            <input
              type="text"
              id="customerName"
              name="customerName"
              value={formData.customerName}
              onChange={handleChange}
              placeholder="Nombre del cliente"
              disabled={loading}
            />
          </div>

          <div className="form-group">
            <label htmlFor="customerEmail">Email del Cliente</label>
            <input
              type="email"
              id="customerEmail"
              name="customerEmail"
              value={formData.customerEmail}
              onChange={handleChange}
              placeholder="cliente@example.com"
              disabled={loading}
            />
          </div>
        </div>

        <div className="form-group">
          <label htmlFor="notes">Notas</label>
          <textarea
            id="notes"
            name="notes"
            value={formData.notes}
            onChange={handleChange}
            rows="4"
            placeholder="Notas adicionales sobre esta licencia..."
            disabled={loading}
          />
        </div>

        <div className="form-actions">
          <button
            type="button"
            onClick={() => navigate('/licenses')}
            className="btn btn-secondary"
            disabled={loading}
          >
            Cancelar
          </button>
          <button type="submit" className="btn btn-primary" disabled={loading}>
            {loading ? 'Guardando...' : isEdit ? 'Actualizar' : 'Crear Licencia'}
          </button>
        </div>
      </form>
    </div>
  );
}

export default LicenseForm;
